import { useApp } from '../store/AppContext'
import { Pill } from './Pill'
import { COVER_REQUEST_STATUS_META, shiftDateLabel } from '../lib/cover'

/**
 * A worker's answer to "can you cover this shift?".
 *
 * Being available is not the same as being booked in, so the shift is only
 * assigned when this Accept is pressed — and only if nobody else got there
 * first, in which case the ask shows as filled instead.
 */
export function CoverResponseInline({
  requestId,
  variant = 'inline',
}: {
  requestId: string
  variant?: 'inline' | 'card'
}) {
  const { currentUser, events, coverRequests, allUsers, respondToCoverRequest, can } = useApp()

  const request = coverRequests.find((r) => r.id === requestId)
  if (!request || request.requestedUserId !== currentUser.id) return null

  const event = events.find((e) => e.id === request.eventId)
  if (!event) return null

  const askedBy = allUsers.find((u) => u.id === request.requestedByUserId)
  const meta = COVER_REQUEST_STATUS_META[request.status]
  const canRespond = request.status === 'pending' && can('cover_shifts')

  const details = variant === 'card' && (
    <div className="mb-2">
      <p className="text-sm font-bold text-slate-900">{event.title}</p>
      <p className="text-xs font-semibold text-slate-600">
        {shiftDateLabel(event.date)} · {event.startTime} – {event.endTime}
      </p>
      {askedBy && <p className="mt-0.5 text-[11px] text-slate-500">Asked by {askedBy.name}</p>}
      {request.note && <p className="mt-1 text-xs text-slate-600">“{request.note}”</p>}
    </div>
  )

  if (!canRespond) {
    return (
      <div className={variant === 'card' ? 'rounded-2xl border border-slate-200 bg-white p-3.5 shadow-card' : 'mt-2'}>
        {details}
        <Pill className={meta.style}>{meta.label}</Pill>
      </div>
    )
  }

  return (
    <div className={variant === 'card' ? 'rounded-2xl border border-amber-200 bg-amber-50 p-3.5 shadow-card' : 'mt-2'}>
      {details}
      <div className="flex gap-2">
        <button
          onClick={() => respondToCoverRequest(requestId, 'declined')}
          className="flex-1 rounded-xl border border-slate-300 bg-white py-2 text-xs font-bold text-slate-600"
        >
          Can't do it
        </button>
        <button
          onClick={() => respondToCoverRequest(requestId, 'accepted')}
          className="flex-1 rounded-xl bg-emerald-600 py-2 text-xs font-bold text-white"
        >
          Yes, I can cover
        </button>
      </div>
    </div>
  )
}

